import { useEffect, useRef } from "react";
import { projects } from "../data/index";

function ProjectCard({ project, index }) {
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.1 },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      style={{
        opacity: 0,
        transform: "translateY(50px)",
        transition: `all 0.7s ease ${index * 0.15}s`,
        background: "#242424",
        border: "1px solid #2e2e2e",
        borderRadius: "20px",
        padding: "40px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#22d3ee";
        e.currentTarget.style.boxShadow = "0 20px 60pxrgba(34, 211, 238),0.08)";
        e.currentTarget.style.transform = "translateY(-6px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#2e2e2e";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* glow blob */}
      <div
        style={{
          position: "absolute",
          top: "-60px",
          right: "-60px",
          width: "200px",
          height: "200px",
          background:
            "radial-gradient(circle,rgba(34, 211, 238),0.05) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      {/* header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            background: "rgba(57,255,20,0.1)",
            border: "1px solidrgba(34, 211, 238),0.2)",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.4rem",
          }}
        >
          {index === 0 ? "🧠" : index === 1 ? "🌴" : "🍴"}
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <a
            href={project.github}
            target="_blank"
            rel="noreferrer"
            style={{
              color: "#666",
              fontSize: "0.85rem",
              fontWeight: 600,
              textDecoration: "none",
              padding: "6px 14px",
              border: "1px solid #2e2e2e",
              borderRadius: "8px",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#22d3ee";
              e.currentTarget.style.color = "#22d3ee";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#2e2e2e";
              e.currentTarget.style.color = "#666";
            }}
          >
            GitHub ↗
          </a>
          <a
            href={project.live}
            target="_blank"
            rel="noreferrer"
            style={{
              color: "#1a1a1a",
              fontSize: "0.85rem",
              fontWeight: 700,
              textDecoration: "none",
              padding: "6px 14px",
              background: "#22d3ee",
              borderRadius: "8px",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                "0 0 20pxrgba(34, 211, 238),0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Live ↗
          </a>
        </div>
      </div>

      {/* title */}
      <div>
        <h3
          style={{
            color: "#f5f5f5",
            fontSize: "1.4rem",
            fontWeight: 800,
            marginBottom: "10px",
            letterSpacing: "-0.5px",
          }}
        >
          {project.title}
        </h3>
        <p
          style={{
            color: "#a0a0a0",
            fontSize: "0.95rem",
            lineHeight: 1.8,
          }}
        >
          {project.description}
        </p>
      </div>

      {/* highlights */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {project.highlights.map((highlight) => (
          <div
            key={highlight}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
            }}
          >
            <span
              style={{
                color: "#22d3ee",
                fontSize: "0.85rem",
                marginTop: "2px",
                flexShrink: 0,
              }}
            >
              ▸
            </span>
            <span
              style={{
                color: "#a0a0a0",
                fontSize: "0.875rem",
                lineHeight: 1.6,
              }}
            >
              {highlight}
            </span>
          </div>
        ))}
      </div>

      {/* tech tags */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          marginTop: "auto",
          paddingTop: "20px",
          borderTop: "1px solid #2e2e2e",
        }}
      >
        {project.tech.map((t) => (
          <span
            key={t}
            style={{
              background: "rgba(57,255,20,0.07)",
              border: "1px solidrgba(34, 211, 238),0.15)",
              color: "#22d3ee",
              fontSize: "0.78rem",
              fontWeight: 600,
              padding: "4px 12px",
              borderRadius: "50px",
              letterSpacing: "0.5px",
            }}
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Projects() {
  const headerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.2 },
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="projects"
      style={{
        padding: "120px 24px",
        maxWidth: "1100px",
        margin: "0 auto",
      }}
    >
      <div
        ref={headerRef}
        style={{
          opacity: 0,
          transform: "translateY(30px)",
          transition: "all 0.7s ease",
          marginBottom: "60px",
        }}
      >
        <p
          style={{
            color: "#22d3ee",
            fontSize: "0.85rem",
            fontWeight: 600,
            letterSpacing: "3px",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          Projects
        </p>

        <h2
          style={{
            fontSize: "clamp(2rem, 4vw, 2.8rem)",
            fontWeight: 800,
            color: "#f5f5f5",
            marginBottom: "16px",
            letterSpacing: "-1px",
          }}
        >
          Things I have built
        </h2>

        <p
          style={{
            color: "#a0a0a0",
            fontSize: "1.05rem",
            maxWidth: "560px",
            lineHeight: 1.8,
          }}
        >
          Every project here is a deliberate engineering decision, not just
          something that works but something that is built the right way.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "24px",
        }}
      >
        {projects.map((project, index) => (
          <ProjectCard key={project.id} project={project} index={index} />
        ))}
      </div>

      <div
        style={{
          marginTop: "60px",
          padding: "40px",
          background: "#242424",
          border: "1px solid #2e2e2e",
          borderRadius: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
        }}
      >
        <div>
          <h3
            style={{
              color: "#f5f5f5",
              fontWeight: 700,
              fontSize: "1.2rem",
              marginBottom: "8px",
            }}
          >
            Want to see more?
          </h3>
          <p style={{ color: "#a0a0a0", fontSize: "0.95rem" }}>
            All my projects are open source. Visit my GitHub to explore the code
            behind everything I build.
          </p>
        </div>
        <a
          href="https://github.com/TheNarh"
          target="_blank"
          rel="noreferrer"
          style={{
            background: "#22d3ee",
            color: "#1a1a1a",
            padding: "14px 28px",
            borderRadius: "12px",
            fontSize: "0.95rem",
            fontWeight: 700,
            textDecoration: "none",
            whiteSpace: "nowrap",
            transition: "all 0.3s ease",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 0 30pxrgba(34, 211, 238),0.4)";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          View GitHub →
        </a>
      </div>
    </section>
  );
}
